import moment from 'moment'
import { memo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { ON } from 'utils/constants'
import {
  useUpdateNotificationMutation,
  useUpdateRoomChannelApparanceMutation,
} from 'app/services/room.service'
import { FixMeLater } from 'types'
import colors from 'utils/colors'
import { roomsActions } from 'app/slices/rooms.slice'

import Switch from 'components/switch/Switch'
import CustomText from 'components/text/CustomText'
import useInfoModal from 'utils/hooks/useInfoModal'
import { BUTTONS, FORM, MESSAGES, MODALS } from 'locales/locales'
import { useTranslation } from 'react-i18next'
import { conversationActions } from 'app/slices/conversations.slice'


import { RoomTypes } from 'types/rooms.types'

interface IGeneralProps {
  roomId: string
  type: string
  phoneNumber: string
  createdBy?: string
  createdAt?: string
  isMuted: boolean
  isAppear: boolean
  isMyRoom: boolean
  notifyOnlyMentions?: boolean
  onClose: () => void
}

const General = ({
  roomId,
  type,
  phoneNumber,
  createdBy,
  createdAt,
  isMuted,
  isMyRoom,
  isAppear,
  notifyOnlyMentions,
  onClose,
}: IGeneralProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { openModal, closeModal, Modal } = useInfoModal()
  const [updateRoomChannelApparance] = useUpdateRoomChannelApparanceMutation()


  const [updateNotification] = useUpdateNotificationMutation();
  const [notificationSettings, setNotificationSettings] = useState({
    isMuted: isMuted || false,
    notifyOnlyMentions: notifyOnlyMentions || false,
  });

  const updateNotificationSettings = (updatedSettings: Partial<typeof notificationSettings>) => {
    setNotificationSettings((prevSettings) => ({ ...prevSettings, ...updatedSettings }));
    updateNotification({ roomId, ...notificationSettings, ...updatedSettings });
  };

  const handleToggleNotification = (updatedNotification: boolean) => {
    const isMuted = updatedNotification;
    const notifyOnlyMentions = isMuted ? false : notificationSettings.notifyOnlyMentions;

    updateNotificationSettings({ isMuted, notifyOnlyMentions });
  };

  const handleToggleNotifyMentionsOnly = (updatedNotifyMentionOnly: boolean) => {
    const newNotifyOnlyMentions = !updatedNotifyMentionOnly;
    updateNotificationSettings({ notifyOnlyMentions: newNotifyOnlyMentions });
  };


  const onApproveDisappearChannel = async () => {
    const result: FixMeLater = await updateRoomChannelApparance({
      roomId,
      isAppear: false,
    })
    if (result.data) {
      dispatch(roomsActions.updateApperance(result.data))
      dispatch(conversationActions.removeAllDraftAndFailedMessages(result.data.roomId))
      navigate(result.data.redirectUrl)
      closeModal()
      onClose()
    }
  }

  const onApproveAppearChannel = async () => {
    try {
      const result: FixMeLater = await updateRoomChannelApparance({
        roomId,
        isAppear: true,
      })
      dispatch(roomsActions.updateApperance(result.data))
      closeModal()
      onClose()
    } catch (e) {
      console.log(e)
    }
  }

  const onDisappearConversation = () => {
    openModal({
      title: t(MODALS.DISAPPEAR_TITLE) as string,
      description: t(MODALS.DISAPPEAR_DESCRIPTION) as string,
      okTitle: t(BUTTONS.DISAPPEAR) as string,
      onCancel: closeModal,
      onOk: onApproveDisappearChannel,
    })
  }

  const onAppearConversation = () => {
    openModal({
      title: t(MODALS.APPEAR_TITLE) as string,
      description: t(MODALS.APPEAR_DESCRIPTION) as string,
      okTitle: t(BUTTONS.APPEAR) as string,
      onCancel: closeModal,
      onOk: onApproveAppearChannel,
    })
  }





  return (
    <Wrapper>
      {createdBy && (
        <Row direction="column" gap={10} marginBottom={10}>
          <CustomText typography="header3">{t(MESSAGES.CREATED_BY)}</CustomText>
          <CustomText typography="body3">
            {createdBy + ON + moment(createdAt).format('DD MMM yyyy HH:mm')}
          </CustomText>
        </Row>
      )}
     
      {phoneNumber && (
        <Row direction="column" gap={10}>
          <CustomText typography="header3">{t(FORM.PHONE_NUMBER)}</CustomText>
          <CustomText typography="body3">{phoneNumber}</CustomText>
        </Row>
      )}

  
      {!isMyRoom && type !== RoomTypes.OTR && (
        <Row align="center" justify="space-between" marginBottom={20}>
          <CustomText typography="body4">{t(BUTTONS.NOTIFICATIONS)}</CustomText>
          <Switch checked={!notificationSettings.isMuted} onChange={handleToggleNotification} />
        </Row>
      )}
      {!isMyRoom && type === 'GM' && !notificationSettings.isMuted  && (
        <Row align="center" justify="space-between" marginBottom={10}>
          <CustomText typography="body4">{t(BUTTONS.MENTIONS_ONLY)}</CustomText>
          <Switch
            checked={notificationSettings.notifyOnlyMentions}
            onChange={handleToggleNotifyMentionsOnly}
          />
        </Row>
      )}
      {!isMyRoom && isAppear && type !== RoomTypes.OTR && (
        <Row marginBottom={10} direction="column">
          <CustomText
            typography="body4"
            color={colors.red}
            cursor="pointer"
            onClick={onDisappearConversation}
          >
            {t(BUTTONS.DISAPPEAR_CONVERSATION)}
          </CustomText>
        </Row>
      )}
      {!isMyRoom && !isAppear && type !== RoomTypes.OTR && (
        <Row marginBottom={10} direction="column">
          <CustomText
            typography="body4"
            color={colors.red}
            cursor="pointer"
            onClick={onAppearConversation}
          >
            {t(BUTTONS.APPEAR_CONVERSATION)}
          </CustomText>
        </Row>
      )}

      {Modal}
    </Wrapper>
  )
}

export default memo(General)

const Wrapper = styled.div``

const Row = styled.div.attrs(
  (props: {
    direction?: string
    gap: number
    marginBottom: number
    align: string
    justify: string
  }) => props,
)`
  display: flex;
  flex-direction: ${({ direction }) => direction || 'row'};
  align-items: ${({ align }) => align || 'flex-start'};
  justify-content: ${({ justify }) => justify || 'flex-start'};
  gap: ${({ gap }) => gap || 0}px;
  margin-bottom: ${({ marginBottom }) => marginBottom || 0}px;
`
